import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  Header,
  SpaceBetween,
  ContentLayout,
  Container,
  FormField,
  Input,
  Textarea,
  Button,
  ColumnLayout,
  Box,
  Flashbar
} from '@cloudscape-design/components';
import { TrajectoryLoader } from '../common/Loaders';
import { useCapabilitiesStore } from 'src/store/capability-store';
import { ROUTES } from 'src/constants';

interface FormData {
  name: string;
  description: string;
  tools: { value: string }[];
  instruction: string;
  inputSchema: string;
  input: string;
  output: string;
  outputSchema: string;
}

const CapabilityEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    capabilities, 
    isLoading, 
    isUpdating, 
    fetchCapabilities, 
    updateCapability 
  } = useCapabilitiesStore();
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      tools: [{ value: '' }],
      instruction: '',
      inputSchema: '',
      input: '',
      output: '',
      outputSchema: ''
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'tools'
  });

  const [notifications, setNotifications] = React.useState<any[]>([]);
  const [capabilityNotFound, setCapabilityNotFound] = React.useState(false);

  // Fetch capabilities on component mount
  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  useEffect(() => {
    if (id && capabilities?.capabilities) {
      const selectedCap = capabilities.capabilities.find((cap: any) => cap.id === id);
      if (selectedCap) {
        reset({
          name: selectedCap.name || '',
          description: selectedCap.description || '',
          instruction: selectedCap.instruction || '',
          inputSchema: selectedCap.inputSchema ? JSON.stringify(selectedCap.inputSchema, null, 2) : '',
          input: selectedCap.input ? JSON.stringify(selectedCap.input, null, 2) : '',
          output: selectedCap.output ? JSON.stringify(selectedCap.output, null, 2) : '',
          outputSchema: selectedCap.outputSchema ? JSON.stringify(selectedCap.outputSchema, null, 2) : '',
          tools: Array.isArray(selectedCap.tools) && selectedCap.tools.length > 0 
            ? selectedCap.tools.map((tool: string) => ({ value: tool }))
            : [{ value: '' }]
        });
        setCapabilityNotFound(false);
      } else {
        setCapabilityNotFound(true);
      }
    }
  }, [id, capabilities, reset]);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const onSubmit = async (data: FormData) => {
    if (!id) return;
    
    try {
      const safeJsonParse = (jsonString: string, fieldName: string) => {
        if (!jsonString.trim()) return {};
        try {
          return JSON.parse(jsonString);
        } catch (error) {
          throw new Error(`Invalid JSON in ${fieldName} field. Please check your syntax.`);
        }
      };

      const updateData = {
        capabilityId: id,
        capability: {
          name: data.name,
          description: data.description,
          tools: data.tools.map(tool => tool.value).filter(tool => tool.trim() !== ''),
          instruction: data.instruction,
          inputSchema: safeJsonParse(data.inputSchema, 'Input Schema'),
          input: safeJsonParse(data.input, 'Input'),
          output: safeJsonParse(data.output, 'Output'),
          outputSchema: safeJsonParse(data.outputSchema, 'Output Schema')
        }
      };
      
      await updateCapability(id, updateData);
      
      setNotifications([{
        type: 'success',
        content: 'Capability updated successfully!',
        dismissible: true,
        id: 'update-success'
      }]);
      
      navigationTimeoutRef.current = setTimeout(() => {
        navigate(ROUTES.CAPABILITIES);
      }, 1500);
    } catch (error: any) {
      setNotifications([{
        type: 'error',
        content: error.message || 'Failed to update capability. Please try again.',
        dismissible: true,
        id: 'update-error'
      }]);
    }
  };

  if (isLoading) {
    return (
      <ContentLayout defaultPadding>
        <div className="loading-state">
          <TrajectoryLoader size="140px" speed="2s" />
          <div className="loading-message">
            Loading capability...
          </div>
        </div>
      </ContentLayout>
    );
  }

  if (!isLoading && capabilityNotFound) {
    return (
      <ContentLayout defaultPadding>
        <Box textAlign="center">
          <SpaceBetween direction="vertical" size="m">
            <Header variant="h2">Capability Not Found</Header>
            <Button onClick={() => navigate(ROUTES.CAPABILITIES)}>Back to Capabilities</Button>
          </SpaceBetween>
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          description="Edit capability settings and tools"
        >
          Edit Capability
        </Header>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <SpaceBetween direction="vertical" size="l">
          <Flashbar items={notifications} />
          
          <Container>
            <SpaceBetween direction="vertical" size="l">
              <FormField label="Capability Name">
                <Input
                  value={watch('name')}
                  onChange={({ detail }) => setValue('name', detail.value)}
                />
              </FormField>

              <FormField label="Description">
                <Textarea
                  value={watch('description')}
                  onChange={({ detail }) => setValue('description', detail.value)}
                  rows={3}
                />
              </FormField>

              <FormField label="Tools">
                <SpaceBetween direction="vertical" size="s">
                  {fields.map((field, index) => (
                    <ColumnLayout key={field.id} columns={2} variant="text-grid">
                      <Input
                        value={watch(`tools.${index}.value`)}
                        onChange={({ detail }) => setValue(`tools.${index}.value`, detail.value)}
                      />
                      <Button
                        variant="normal"
                        onClick={(e) => {
                          e.preventDefault();
                          remove(index);
                        }}
                        disabled={fields.length === 1}
                      >
                        Remove
                      </Button>
                    </ColumnLayout>
                  ))}
                  <Box textAlign="left">
                    <Button 
                      variant="normal" 
                      onClick={(e) => {
                        e.preventDefault();
                        append({ value: '' });
                      }}
                    >
                      Add Tool
                    </Button>
                  </Box>
                </SpaceBetween>
              </FormField>

              <FormField label="Instruction">
                <Textarea
                  value={watch('instruction')}
                  onChange={({ detail }) => setValue('instruction', detail.value)}
                  rows={8}
                />
              </FormField>

              <FormField label="Input Schema">
                <Textarea
                  value={watch('inputSchema')}
                  onChange={({ detail }) => setValue('inputSchema', detail.value)}
                  rows={8}
                />
              </FormField>

              <FormField label="Input">
                <Textarea
                  value={watch('input')}
                  onChange={({ detail }) => setValue('input', detail.value)}
                  rows={3}
                />
              </FormField>

              <FormField label="Output Schema">
                <Textarea
                  value={watch('outputSchema')}
                  onChange={({ detail }) => setValue('outputSchema', detail.value)}
                  rows={3}
                />
              </FormField>

              <FormField label="Output">
                <Textarea
                  value={watch('output')}
                  onChange={({ detail }) => setValue('output', detail.value)}
                  rows={3}
                />
              </FormField>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                width: '100%'
              }}>
                <div></div>
                <div>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button 
                      variant="normal" 
                      onClick={() => navigate(ROUTES.CAPABILITIES)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={() => handleSubmit(onSubmit)()}
                      loading={isUpdating}
                      disabled={!id}
                    >
                      Update
                    </Button>
                  </SpaceBetween>
                </div>
              </div>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </form>
    </ContentLayout>
  );
};

export default CapabilityEditPage;
